import { ClientLoggingService } from '../../../../../shared/client-logging.service';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  OnChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  inject,
} from '@angular/core';

import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Subject, timer, takeUntil } from 'rxjs';
import {
  IDLE_INTERACTION,
  PREVIEW_AREA_SHAPES,
  PreviewAreaShape,
  PreviewCommandDescriptor,
  PreviewCommandId,
  PreviewFitMode,
  PreviewInteraction,
  interactionLabel,
  previewAreaShape,
  previewCommand,
  toolOf,
} from './preview-commands';

/**
 * Which scale the page's measurements use (Issue #2442).
 *
 * `manual`: a saved calibration. `automatic`: the scale detected in the page transcript. `none`:
 * neither. `invalidated`: a saved calibration that is no longer applied because the page changed.
 */
export type ToolbarScaleStatus = 'manual' | 'automatic' | 'none' | 'invalidated';

/** A command from the toolbar to the Preview (Issue #2542). */
export interface PdfToolbarAction {
  type: PreviewCommandId;
  value?: number; // For setZoom action
  /** For selectAreaShape: the Select area shape that was chosen (Issue #2542). */
  areaShape?: PreviewAreaShape;
}

/**
 * Toolbar overlaying the PDF preview (Issue #2542).
 *
 * Three groups hold the commands by intent: Zoom (zoom in/out, typed percentage, fit modes), in
 * a small dialog because it holds a text field, and the Material menus Page (rotation, page scale, open in new tab) and Tools (ruler, select area). The closed
 * triggers still show what matters without opening anything: the zoom percentage, a badge for a
 * manual, missing or invalidated scale, and the active tool with a one-click exit.
 *
 * The toolbar fades out after a pause. It stays visible while a menu is open, while the zoom field
 * is being edited and while keyboard focus is inside it.
 */
@Component({
  selector: 'app-pdf-toolbar',
  imports: [
    A11yModule,
    OverlayModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    FormsModule,
  ],
  templateUrl: './pdf-toolbar.component.html',
  styleUrls: ['./pdf-toolbar.component.scss'],
})
export class PdfToolbarComponent implements OnInit, OnChanges, OnDestroy {
  private cdr = inject(ChangeDetectorRef);

  @Input() currentScale = 1.0;
  @Input() baseScale = 1.0;
  @Input() isRotating = false;
  /**
   * Whether the reviewer may edit this project (Issue #3313): false for a reader, whose rotation and
   * calibration the server would refuse, so those items are shown disabled; null while unknown.
   */
  @Input() canEdit: boolean | null = null;
  /** The Preview's geometry interaction: idle, a ruler line, or an area selection. */
  @Input() interaction: PreviewInteraction = IDLE_INTERACTION;
  /** The shape Select area starts with when chosen without a shape: the one used last. */
  @Input() areaShape: PreviewAreaShape = 'rectangle';
  /** The Select area shapes offered in the Tools menu (Issues #2439, #2440, #1747). */
  readonly areaShapes = PREVIEW_AREA_SHAPES.filter(option => option.shape !== 'select');
  /** Select & move acts on areas that already exist, so it is listed apart from the shapes. */
  readonly selectAndMove = previewAreaShape('select');
  /** Which scale the page's measurements use (Issue #2442). */
  @Input() scaleStatus: ToolbarScaleStatus = 'none';
  /** The scale in words, e.g. "Manually calibrated · 1:48" or "No scale detected" (Issue #2442). */
  @Input() scaleSummary = '';
  /** True while the detected scale is temporarily shown instead of the manual one (Issue #2442). */
  @Input() comparingScale = false;
  /**
   * The fit mode the page is currently fitted to, or null when it is at some other zoom
   * (Issue #2542). When the Preview does not say, Fit to viewport is inferred from the scale.
   */
  @Input() fitMode: PreviewFitMode | null | undefined = undefined;
  @Output() action = new EventEmitter<PdfToolbarAction>();
  @ViewChild('zoomInput') zoomInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('zoomTrigger', { read: ElementRef }) zoomTriggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('zoomPanel') zoomPanelRef?: ElementRef<HTMLElement>;

  /** Whether the Zoom panel is open. */
  zoomOpen = false;
  /** Opened from the keyboard: focus goes straight to the percentage field. */
  private zoomOpenedByKeyboard = false;
  /** Below the trigger, right edges aligned like the other menus; above it when there is no room. */
  readonly zoomPanelPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
  ];

  zoomInputValue = '';
  isEditingZoom = false;
  /** The text Enter last applied while the zoom field kept focus, so blur does not apply it twice. */
  private appliedZoomInput: string | null = null;

  private destroy$ = new Subject<void>();
  private hideTimer$ = new Subject<void>();
  /** Cancels the pause that follows a toolbar action when another action starts a new one. */
  private interaction$ = new Subject<void>();
  /** True during the pause that follows a command, which outlasts the pause after a plain click. */
  private actionPause = false;
  private readonly HIDE_DELAY = 3000; // 3 seconds
  private _internalVisible = true;
  private isUserInteracting = false;
  private isForceHidden = false;
  private hasFocusWithin = false;
  /** Menus currently open; the toolbar never hides while one is. */
  private openMenus = 0;

  /** The command descriptors, for the template. */
  readonly cmd = previewCommand;

  get internalVisible(): boolean {
    return this._internalVisible;
  }

  get isVisible(): boolean {
    return this._internalVisible;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.menuJustClosed = false;
    this.showToolbar();
  }

  /**
   * Keeps the toolbar visible while keyboard focus is inside it (Issue #1747).
   *
   * The toolbar fades out after a pause and otherwise reappears only on mouse movement, so a
   * keyboard user tabbing to a tool would focus an invisible button.
   */
  @HostListener('focusin')
  onFocusIn(): void {
    this.isForceHidden = false;
    this.hasFocusWithin = true;
    this.hideTimer$.next();
    this._internalVisible = true;
  }

  /** Resumes the normal auto-hide once focus leaves the toolbar. */
  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && (event.currentTarget as HTMLElement | null)?.contains?.(next)) return;
    this.hasFocusWithin = false;
    this.menuJustClosed = false;
    this.startHideTimer();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    // Don't start hide timer immediately on mouse leave
    // Let the document click/mousemove handlers manage visibility
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const element = event.target as HTMLElement;
    this.resultUnderPointer = this.measurementResult(element);

    // Don't auto-show if toolbar was force hidden
    if (this.isForceHidden) return;

    // Check if mouse is within the toolbar area or PDF container
    if (element?.closest('.pdf-toolbar') || this.isOnViewer(element)) {
      this.showToolbar();
    }
  }

  /**
   * True for the page area and for the measurement result docked over it (Issue #2542). The result
   * is drawn beside the page area, not inside it, and working in it is still working in the viewer:
   * the toolbar, which holds the button that leaves the tool, must not fade away meanwhile. On a
   * phone a tap on the result is also the only tap that brings the toolbar back without starting
   * a shape on the page.
   */
  private isOnViewer(element: HTMLElement | null): boolean {
    return !!(element?.closest('.pdf-container') || this.measurementResult(element));
  }

  /** The docked measurement result that contains the element, if any. */
  private measurementResult(element: HTMLElement | null): HTMLElement | null {
    return element?.closest?.<HTMLElement>('[data-testid="preview-measure-result"]') ?? null;
  }

  /**
   * The measurement result the pointer was last seen on, or null once it has moved elsewhere
   * (Issue #2542). A reviewer reading the result, or about to press one of its buttons, holds the
   * pointer still; the toolbar does not fade for as long as the pointer is still there and the
   * result is still on the page.
   */
  private resultUnderPointer: HTMLElement | null = null;

  /** True while the pointer rests on a measurement result that is still shown. */
  private get pointerHeldOnResult(): boolean {
    return !!this.resultUnderPointer?.isConnected;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Check if click is within the toolbar area or PDF container
    const element = event.target as HTMLElement;
    // A tap on a touch screen comes without pointer movement, so the click also says where the pointer is.
    this.resultUnderPointer = this.measurementResult(element);
    if (element?.closest('.zoom-input')) return;
    if (element?.closest('.pdf-toolbar') || element?.closest('.preview-toolbar-menu')) {
      // A click elsewhere in the toolbar commits a zoom that is being typed.
      if (this.isEditingZoom) {
        this.onZoomInputBlur();
      }

      this.isUserInteracting = true;
      this.isForceHidden = false; // Reset force hidden when user clicks toolbar
      this.showToolbar();
      // Reset interaction flag after a short delay, and resume auto-hide: the hide timer does not
      // start while the flag is set, so nothing else would start it until the pointer next moves.
      setTimeout(() => this.endClickPause(), 1000);
    } else {
      // Click outside toolbar - blur zoom input if editing
      if (this.isEditingZoom) {
        this.onZoomInputBlur();
      }

      if (this.isOnViewer(element)) {
        // Don't auto-show if toolbar was force hidden - let the tap handler manage it
        if (!this.isForceHidden) {
          this.showToolbar();
        }
      }
    }
  }

  ngOnInit(): void {
    this.startHideTimer();
    // Update zoom input value initially
    this.zoomInputValue = this.getScalePercentage();
  }

  ngOnChanges(): void {
    // Update zoom input value when scale changes
    if (!this.isEditingZoom) {
      this.zoomInputValue = this.getScalePercentage();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideTimer$.next();
    this.hideTimer$.complete();
    this.interaction$.complete();
  }

  /** Starts or continues Select area with one shape. */
  onAreaShape(shape: PreviewAreaShape): void {
    this.onAction('selectAreaShape', shape);
  }

  /** The shape Select area is drawing now, or null when the tool is not active. */
  get activeAreaShape(): PreviewAreaShape | null {
    return this.interaction.kind === 'area' ? this.interaction.shape : null;
  }

  onAction(actionType: PdfToolbarAction['type'], areaShape?: PreviewAreaShape): void {
    // Cancel zoom input editing if user clicks zoom buttons
    if (this.isEditingZoom && (actionType === 'zoomIn' || actionType === 'zoomOut')) {
      this.isEditingZoom = false;
      // Don't apply the current input value, just cancel editing
      this.zoomInputValue = this.getScalePercentage();
    }

    this.action.emit(areaShape ? { type: actionType, areaShape } : { type: actionType });
    // Keep toolbar visible after action and restart the hide timer with longer delay
    this._internalVisible = true;
    this.isUserInteracting = true;
    this.hideTimer$.next(); // Cancel any existing timer

    // Use a longer delay for button actions to allow rapid clicking. This timer has its own
    // cancel signal: a menu handing focus back to its trigger cancels the hide timer, and if that
    // also cancelled this one the toolbar would stay "interacting", and visible, indefinitely.
    this.interaction$.next();
    this.actionPause = true;
    timer(this.HIDE_DELAY * 2) // 6 seconds instead of 3
      .pipe(takeUntil(this.interaction$))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.actionPause = false;
        this.isUserInteracting = false;
        // An open menu, focus inside the toolbar or a zoom edit still holds it; each of those
        // restarts the hide timer itself when it ends.
        if (this.openMenus > 0 || this.hasFocusWithin || this.isEditingZoom) return;
        // The pointer was active in the viewer a moment ago, for example drawing with the tool
        // just picked: the normal fade applies from here, instead of the toolbar, and the button
        // that leaves the tool, disappearing under a moving pointer (Issue #2542).
        if (this.pointerHeldOnResult || Date.now() - this.lastPointerActivity < this.HIDE_DELAY) {
          this.startHideTimer();
          return;
        }
        this._internalVisible = false;
      });
  }

  /** The short pause after a click in the toolbar is over: resume auto-hide unless something holds it. */
  private endClickPause(): void {
    // A command's longer pause is still running and ends the interaction itself.
    if (this.actionPause) return;
    this.isUserInteracting = false;
    this.startHideTimer();
  }

  /** Sends a command from a menu item, keeping the menu open for repeatable commands such as zoom. */
  onMenuCommand(command: PreviewCommandDescriptor, event?: Event): void {
    if (command.keepsMenuOpen) event?.stopPropagation();
    this.onAction(command.id);
  }

  /** The Zoom trigger was activated. A click from the keyboard has no click count. */
  toggleZoomPanel(event?: MouseEvent): void {
    if (this.zoomOpen) {
      this.closeZoomPanel();
      return;
    }
    this.zoomOpenedByKeyboard = event?.detail === 0;
    this.zoomOpen = true;
    this.onMenuOpened();
  }

  /**
   * The Zoom panel is on screen. From the keyboard, focus goes to the percentage field, ready to
   * type; from a pointer it goes to the panel itself, so a phone does not raise its keyboard.
   */
  onZoomPanelAttached(): void {
    setTimeout(() => {
      if (!this.zoomOpen) return;
      if (this.zoomOpenedByKeyboard) this.focusZoomInput();
      else this.zoomPanelRef?.nativeElement.focus({ preventScroll: true });
    }, 0);
  }

  /** Closes the Zoom panel and, like a menu, hands focus back to its trigger. */
  closeZoomPanel(): void {
    if (!this.zoomOpen) return;
    this.zoomOpen = false;
    this.onMenuClosed();
    this.zoomTriggerRef?.nativeElement.focus({ preventScroll: true });
    this.cdr.markForCheck();
  }

  /** Escape closes the Zoom panel; the key is marked as handled so no tool or panel acts on it too. */
  onZoomPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    event.preventDefault();
    this.closeZoomPanel();
  }

  /** A fit was chosen: the page is fitted and the panel closes, as a menu item would. */
  onFitChosen(fit: PreviewCommandDescriptor): void {
    this.onAction(fit.id);
    this.closeZoomPanel();
  }

  /** Only one fit is in the Tab order: the checked one, or the first when none is. */
  fitTabIndex(index: number): number {
    const checked = this.fitCommands.findIndex(fit => fit.mode === this.selectedFit);
    return index === (checked < 0 ? 0 : checked) ? 0 : -1;
  }

  /** Arrow keys, Home and End move between the fits; Enter or Space chooses one. */
  onFitKeydown(event: KeyboardEvent, group: HTMLElement): void {
    const radios = Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
    const current = radios.indexOf(event.target as HTMLButtonElement);
    if (current < 0) return;
    const last = radios.length - 1;
    const next =
      event.key === 'ArrowDown' || event.key === 'ArrowRight'
        ? current === last
          ? 0
          : current + 1
        : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
          ? current === 0
            ? last
            : current - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : -1;
    if (next < 0) return;
    event.preventDefault();
    radios[next].focus();
  }

  /** A menu opened: keep the toolbar on screen until it closes. */
  onMenuOpened(): void {
    this.openMenus++;
    this.isForceHidden = false;
    this.hideTimer$.next();
    this._internalVisible = true;
  }

  /** A menu closed: resume auto-hide unless focus came back into the toolbar. */
  onMenuClosed(): void {
    this.openMenus = Math.max(0, this.openMenus - 1);
    this.menuJustClosed = true;
    if (this.isEditingZoom) {
      this.isEditingZoom = false;
      this.zoomInputValue = this.getScalePercentage();
    }
    this.startHideTimer();
  }

  /**
   * True from the moment a menu closes until the pointer enters the toolbar or focus leaves it. The
   * menu hands focus back to its trigger as keyboard focus, which would show the trigger's tooltip.
   */
  private menuJustClosed = false;

  /**
   * Whether the group triggers keep their tooltips hidden (Issue #2542). A visible Material tooltip
   * takes the next Escape for itself. While a tool is active, or just after a menu was closed with
   * Escape, that key press belongs to the tool: it must cancel the geometry or leave the tool on
   * the first press. Found on the deployed build, where it failed about one time in six.
   */
  get triggerTooltipsOff(): boolean {
    return this.menuJustClosed || !!this.activeTool;
  }

  /** True while any toolbar menu is open. */
  get menuOpen(): boolean {
    return this.openMenus > 0;
  }

  // ── Tools ─────────────────────────────────────────────────────

  get activeTool(): 'ruler' | 'area' | null {
    return toolOf(this.interaction);
  }

  /** True while the ruler line is the reference of a calibration being entered. */
  get calibrating(): boolean {
    return this.interaction.kind === 'line' && this.interaction.operation === 'scaleReference';
  }

  /** Text of the Tools trigger: the active tool, or "Tools". */
  get toolsLabel(): string {
    return interactionLabel(this.interaction);
  }

  /** What each Tools menu entry is for; shown as its tooltip and read out as its description. */
  readonly toolHints = {
    ruler: 'Measure lengths; use a line as the scale reference',
    area: 'Measure areas or ask chat about one',
    selectAndMove: 'Pick an area to move it or send it to chat',
  } as const;

  /**
   * The page scale as one line for the Settings menu: the scale in words when there is one, such
   * as "Manually calibrated · 1:48", and otherwise the bare state, such as "No scale". The state
   * word is put first only when the words do not already say it.
   */
  get scaleLine(): string {
    const state = this.scaleIndicatorText;
    const detail = this.scaleDetail;
    if (!this.scaleSummary.trim()) return state;
    // While the detected scale is being compared, the words name the scale but not that it is a
    // comparison, so the state leads.
    if (this.comparingScale) return detail ? `${state} · ${detail}` : state;
    return this.scaleSummary.trim();
  }

  /** Accessible name of the Tools trigger, which says which tool is active. */
  get toolsAriaLabel(): string {
    if (!this.activeTool) return 'Tools';
    // The button shows only an icon, so its name also says which Select area shape is in use.
    const shape = this.interaction.kind === 'area' ? `, ${previewAreaShape(this.interaction.shape).label}` : '';
    return `Tools. Active tool: ${this.toolsLabel}${shape}`;
  }

  get toolsIcon(): string {
    if (this.calibrating) return 'architecture';
    if (this.activeTool === 'ruler') return 'straighten';
    if (this.interaction.kind === 'area') return previewAreaShape(this.interaction.shape).icon;
    return 'construction';
  }

  // ── Page ──────────────────────────────────────────────────────

  /**
   * True when the Page trigger shows a warning badge: the page has no scale, or its saved
   * calibration no longer applies (UX note 3). A valid scale, detected or manual, needs no badge.
   */
  get showScaleBadge(): boolean {
    return this.scaleStatus === 'none' || this.scaleStatus === 'invalidated';
  }

  /**
   * Accessible name of the Page trigger. A screen reader does not announce the badge, so the scale
   * state is part of the name, e.g. "Page — scale invalidated".
   */
  get pageAriaLabel(): string {
    const parts = ['Settings'];
    if (this.isRotating) parts.push('rotating');
    if (this.scaleNotice) parts.push(this.scaleNotice);
    else if (this.scaleStatus === 'manual') parts.push('manually calibrated');
    return parts.join(', ');
  }

  /**
   * What the dot on the Settings button means and what to do about it, or null when there is no
   * dot (requester's feedback on #2542: the dot was unexplained). The same words are the button's
   * tooltip and part of its accessible name.
   */
  get scaleNotice(): string | null {
    if (this.scaleStatus === 'none') return 'No scale for this page. Open to calibrate.';
    if (this.scaleStatus === 'invalidated') return 'Calibration no longer applied. Open to recalibrate.';
    return null;
  }

  /** Tooltip of the Settings button: its name, and the meaning of its dot when it has one. */
  get settingsTooltip(): string {
    return this.scaleNotice ? `Settings · ${this.scaleNotice}` : 'Settings';
  }

  /** Label of the calibrate menu item: what choosing it does now. */
  get calibrateMenuLabel(): string {
    if (this.calibrating) return 'Cancel calibration';
    return this.scaleStatus === 'manual' || this.scaleStatus === 'invalidated'
      ? 'Recalibrate scale'
      : 'Calibrate scale';
  }

  /** Icon of the scale-status line in the Page menu. */
  get scaleStatusIcon(): string {
    switch (this.scaleStatus) {
      case 'manual':
        return 'verified';
      case 'invalidated':
        return 'warning';
      case 'none':
        return 'help_outline';
      default:
        return 'straighten';
    }
  }

  /** When the pointer last moved or clicked in the viewer, as a `Date.now()` value. */
  private lastPointerActivity = 0;

  private showToolbar(): void {
    this.lastPointerActivity = Date.now();
    this._internalVisible = true;
    this.startHideTimer();
  }

  private startHideTimer(): void {
    // Don't start hide timer if user is actively interacting, or keyboard focus is inside the toolbar
    if (this.isUserInteracting || this.hasFocusWithin || this.openMenus > 0 || this.isEditingZoom) {
      return;
    }

    this.hideTimer$.next();
    timer(this.HIDE_DELAY)
      .pipe(takeUntil(this.hideTimer$))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // The pointer is resting on the measurement result: look again later instead of fading.
        if (this.pointerHeldOnResult) {
          this.startHideTimer();
          return;
        }
        this._internalVisible = false;
      });
  }

  // Public methods for external control
  /** Immediately hides the toolbar and suppresses auto-show on mouse movement until the user clicks the toolbar again. */
  public forceHide(): void {
    ClientLoggingService.log('INFO', 'forceHide() called');
    this._internalVisible = false;
    this.isForceHidden = true; // Mark as force hidden
    this.hideTimer$.next(); // Cancel any pending hide timer
  }

  /** Makes the toolbar visible, clears the force-hidden state, and starts a new auto-hide timer. */
  public forceShow(): void {
    ClientLoggingService.log('INFO', 'forceShow() called');
    this._internalVisible = true;
    this.isForceHidden = false; // Reset force hidden flag
    this.hideTimer$.next(); // Cancel any pending hide timer
    this.startHideTimer(); // Start a new hide timer
  }

  /** Text of the saved-calibration line in the Page menu. */
  get scaleIndicatorText(): string {
    if (this.scaleStatus === 'invalidated') return 'Calibration not applied';
    if (this.scaleStatus === 'none') return 'No scale';
    if (this.scaleStatus === 'automatic') return 'Detected scale';
    return this.comparingScale ? 'Comparing detected scale' : 'Manually calibrated';
  }

  /**
   * The second line of the scale status in the Page menu: the summary without the words the first
   * line already says, so "Manually calibrated · 1:48" under "Manually calibrated" reads "1:48".
   */
  get scaleDetail(): string {
    const title = this.scaleIndicatorText;
    const summary = this.scaleSummary.trim();
    if (!summary.toLowerCase().startsWith(title.toLowerCase())) return summary;
    const rest = summary.slice(title.length);
    if (!rest.trim()) return '';
    // Only a repeated heading is dropped: "No scale detected" under "No scale" stays whole.
    const separated = /^\s*[·:,–-]\s*(.*)$/.exec(rest);
    return separated ? separated[1] : summary;
  }

  /** The three fit commands with the fit mode each one selects, in menu order. */
  readonly fitCommands: (PreviewCommandDescriptor & { mode: PreviewFitMode })[] = [
    { ...previewCommand('fitToViewport'), mode: 'viewport' },
    { ...previewCommand('fitToWidth'), mode: 'width' },
    { ...previewCommand('fitToHeight'), mode: 'height' },
  ];

  /** The fit mode shown as checked in the Zoom panel, if any. */
  get selectedFit(): PreviewFitMode | null {
    if (this.fitMode !== undefined) return this.fitMode;
    return this.isAtBaseScale() ? 'viewport' : null;
  }

  getScalePercentage(): string {
    return `${Math.round(this.currentScale * 100)}%`;
  }

  isAtBaseScale(): boolean {
    return Math.abs(this.currentScale - this.baseScale) < 0.01;
  }

  /** The zoom field in the Zoom menu took focus: start editing from the current percentage. */
  onZoomInputFocus(): void {
    if (this.isEditingZoom) return;
    this.isEditingZoom = true;
    this.appliedZoomInput = null;
    this.zoomInputValue = this.getScalePercentage();
    this.hideTimer$.next();
    this._internalVisible = true;
    // Selected again after a click has placed the cursor, but only while the field still shows the
    // current zoom: a digit typed straight after opening the panel from the keyboard must not be
    // selected and then replaced by the next one ("150" became "50%" on the deployed build).
    const shown = this.zoomInputValue;
    setTimeout(() => {
      const input = this.zoomInputRef?.nativeElement;
      if (input && input.value === shown) input.select();
    }, 0);
  }

  /** Puts keyboard focus in the zoom field, e.g. from the "Adjust zoom level" caption. */
  focusZoomInput(event?: Event): void {
    event?.stopPropagation();
    this.cdr.detectChanges();
    setTimeout(() => {
      const input = this.zoomInputRef?.nativeElement;
      if (!input) {
        ClientLoggingService.log('INFO', 'Zoom input is not rendered');
        return;
      }
      input.focus();
      input.select();
    }, 0);
  }

  onZoomInputBlur(): void {
    if (!this.isEditingZoom) return;
    this.isEditingZoom = false;
    // Enter already applied this exact text; only a value typed since then is new.
    if (this.zoomInputValue !== this.appliedZoomInput) this.applyZoomInput();
    this.appliedZoomInput = null;
  }

  /**
   * Keys typed in the zoom field stay in the field, so nothing else in the Preview acts on them
   * mid-number. Enter applies the value and keeps the panel open; Escape restores the current value
   * and lets the panel close.
   */
  onZoomInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isEditingZoom = false;
      this.zoomInputValue = this.getScalePercentage();
      return;
    }
    if (event.key === 'Tab') return;
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      // The field keeps focus after Enter, so no new focus event will start another edit. Stay in
      // editing mode, or a second value typed afterwards would never be applied on blur.
      this.applyZoomInput();
      this.appliedZoomInput = this.zoomInputValue;
      this.zoomInputRef?.nativeElement.select();
    }
  }

  private applyZoomInput(): void {
    const value = this.zoomInputValue.replace('%', '').trim();
    const numericValue = parseFloat(value);

    if (!isNaN(numericValue) && numericValue >= 20 && numericValue <= 500) {
      const scale = numericValue / 100;
      this.action.emit({ type: 'setZoom', value: scale });
    } else {
      // Reset to current value if invalid
      this.zoomInputValue = this.getScalePercentage();
    }
  }
}
